import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ApiError, PaginationModel, handleError } from '../../../../api/base';
import RolesService from '../../../../api/roles';
import {
  CreateRoleRequest,
  UpdateRoleRequest,
} from '../../../../api/roles/reqres';
import { RoleModel } from '../../../../models/role';
import { ListModelParams } from '../../../../types';
import { ListRequestParams } from '../../../../types/list';
import { convertParamsToQuery } from '../../../../util';
import { RootState } from '../../../index';
export interface RolesState {
  value: RoleModel[];
  allRoles: RoleModel[];
  pagination: PaginationModel | null;
  fetchingRoles: boolean;
  error: { code: number; details?: string; message: string } | null;
  searchingRoles: boolean;
  savingRole: boolean;
  totalCount: number;
  // centralized URL-state controls
  page: number;
  pageSize: number;
  search: string;
  sort: string; // e.g., 'updated_at:desc'
}

const initialState: RolesState = {
  value: [],
  allRoles: [],
  pagination: null,
  error: null,
  fetchingRoles: false,
  searchingRoles: false,
  savingRole: false,
  totalCount: 0,
  // defaults for centralized URL-state
  page: 1,
  pageSize: 20,
  search: '',
  sort: 'updated_at:desc',
};

const rolesParams: ListModelParams = {
  _limit: 20,
  _order_by: 'updated_at:desc',
  _offset: 0,
  _columns: 'permissions.*',
};

export const fetchRoles = createAsyncThunk('roles/fetchRoles', async () => {
  const queryString = convertParamsToQuery(rolesParams);
  const resp = await RolesService().listRoles(queryString);
  return resp;
});

export const fetchRolesWithParams = createAsyncThunk(
  'roles/fetchRolesWithParams',
  async ({ params }: { params: ListRequestParams }, { rejectWithValue }) => {
    try {
      const queryString = convertParamsToQuery(params);
      const resp = await RolesService().listRoles(queryString);
      return resp;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createRole = createAsyncThunk(
  'roles/createRole',
  async (role: CreateRoleRequest, { dispatch, rejectWithValue }) => {
    try {
      const resp = await RolesService().createRole(role);
      await dispatch(fetchRoles());
      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async (
    { roleId, role }: { roleId: string; role: UpdateRoleRequest },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const resp = await RolesService().updateRole(roleId, role);

      await dispatch(fetchRoles());

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteRole = createAsyncThunk(
  'role/deleteRole',
  async (roleId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const resp = await RolesService().deleteRole(roleId);

      // refresh current page respecting centralized controls
      const {
        roles: { page, pageSize, search, sort },
      } = getState() as RootState;
      const params: ListRequestParams = {
        _limit: pageSize,
        _offset: (page - 1) * pageSize,
        _order_by: sort || 'updated_at:desc',
        _columns: 'permissions.*',
        ...(search ? { name: `*${String(search).toLowerCase()}*` } : {}),
      };
      await dispatch(fetchRolesWithParams({ params }));

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchRolesByNameWithParams = createAsyncThunk(
  'roles/searchRolesByNameWithParams',
  async ({ params }: { params: ListRequestParams }, { rejectWithValue }) => {
    try {
      const queryString = convertParamsToQuery(params);
      return await RolesService().listRoles(queryString);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    hydrateFromUrl: (
      state,
      {
        payload,
      }: {
        payload: {
          page?: number;
          pageSize?: number;
          search?: string;
          sort?: string;
        };
      }
    ) => {
      if (payload.page !== undefined) state.page = payload.page;
      if (payload.pageSize !== undefined) state.pageSize = payload.pageSize;
      if (payload.search !== undefined) state.search = payload.search ?? '';
      if (payload.sort !== undefined) state.sort = payload.sort ?? '';
    },
    setRoleSearchQuery: (state, { payload }: { payload: string }) => {
      state.search = payload ?? '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.fetchingRoles = true;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.fetchingRoles = false;
        state.value = action.payload.data;
        state.allRoles = action.payload.data;
        state.pagination = action.payload.pagination;
        state.totalCount =
          action.payload.pagination?.count ?? action.payload.data.length;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingRoles = false;
      });

    builder
      .addCase(fetchRolesWithParams.pending, (state) => {
        state.fetchingRoles = true;
      })
      .addCase(fetchRolesWithParams.fulfilled, (state, action) => {
        state.fetchingRoles = false;
        state.value = action.payload.data;
        state.allRoles = action.payload.data;
        state.pagination = action.payload.pagination;
        state.totalCount =
          action.payload.pagination?.count ?? action.payload.data.length;
      })
      .addCase(fetchRolesWithParams.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingRoles = false;
      });

    builder
      .addCase(searchRolesByNameWithParams.pending, (state) => {
        state.searchingRoles = true;
      })
      .addCase(searchRolesByNameWithParams.fulfilled, (state, action) => {
        state.searchingRoles = false;
        if (action.payload) {
          state.value = action.payload?.data;
          state.pagination = action.payload?.pagination;
          state.totalCount =
            action.payload?.pagination?.count ||
            action.payload?.data.length ||
            0;
        }
      })
      .addCase(searchRolesByNameWithParams.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.searchingRoles = false;
      });

    builder
      .addCase(createRole.pending, (state) => {
        state.savingRole = true;
      })
      .addCase(createRole.fulfilled, (state) => {
        state.savingRole = false;
      })
      .addCase(createRole.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingRole = false;
      });

    builder
      .addCase(updateRole.pending, (state) => {
        state.savingRole = true;
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.savingRole = false;
      })
      .addCase(updateRole.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingRole = false;
      });
  },
});

export const { hydrateFromUrl, setRoleSearchQuery } = rolesSlice.actions;
export default rolesSlice.reducer;
